import {
  LBBuilderIcon,
  LBConnectIcon,
  LBBadge,
  LBRocketIcon,
  LBTargetIcon,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger
} from '@linearb/buzz'
import type { ComponentProps, ComponentType, SVGProps } from 'react'
import { getRoleScopeDefinition, splitScopes } from '../constants/scopes'
import type { RoleScopeName } from '../types/scope.types'

type BadgeVariant = ComponentProps<typeof LBBadge>['variant']

const ROLE_ICONS: Record<
  RoleScopeName,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  bo_engineering: LBBuilderIcon,
  bo_product: LBTargetIcon,
  bo_gtm: LBRocketIcon,
  bo_techgtm: LBConnectIcon
}

interface BackofficeScopeBadgesProps {
  scopes?: string[]
  /**
   * Capability badges only — roles keep their own styling, since they are never
   * the thing being granted or revoked.
   */
  capabilityVariant?: BadgeVariant
}

/**
 * RoleScopeBadge - the bare role badge, no tooltip.
 *
 * Exported for ScopeFilter, whose rows sit inside a <button> and so cannot nest
 * the focusable tooltip trigger below.
 */
export const RoleScopeBadge: React.FC<{ name: string }> = ({ name }) => {
  const role = getRoleScopeDefinition(name)
  const Icon = role && ROLE_ICONS[role.name]

  return (
    <LBBadge
      variant='outline'
      className='min-w-[6rem] justify-center gap-1 border-border bg-muted/50 text-xs font-medium text-muted-foreground whitespace-nowrap'
    >
      {Icon && <Icon className='size-3.5 opacity-70' />}
      {role?.label ?? name}
    </LBBadge>
  )
}

/**
 * The same badge, explained on hover. Exported so screens that show roles on their
 * own (without the capability badges below) still say where the role comes from.
 */
export const RoleScopeBadgeWithTooltip: React.FC<{ name: string }> = ({
  name
}) => {
  const role = getRoleScopeDefinition(name)

  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <span tabIndex={0}>
          <RoleScopeBadge name={name} />
        </span>
      </LBTooltipTrigger>
      <LBTooltipContent className='max-w-64'>
        {/* splitScopes only ever hands this component a known role, but the
            fallback keeps the copy readable if it is ever called directly. */}
        {role ? (
          <div className='space-y-1 text-xs leading-relaxed'>
            <div className='font-medium'>{role.label} role</div>
            <div className='text-muted-foreground'>
              Synced from {role.group}
            </div>
          </div>
        ) : (
          `${name} role`
        )}
      </LBTooltipContent>
    </LBTooltip>
  )
}

/**
 * BackofficeScopeBadges - renders a stored `backoffice_scopes` value, keeping
 * the editable capabilities visually apart from the Google group–managed roles.
 *
 * Every screen that shows backoffice_scopes should use this rather than
 * splitting the array itself, so the role treatment can't go missing from one
 * of them.
 */
const BackofficeScopeBadges: React.FC<BackofficeScopeBadgesProps> = ({
  scopes = [],
  capabilityVariant = 'secondary'
}) => {
  if (!scopes || scopes.length === 0) {
    return (
      <span className='text-sm text-muted-foreground italic'>No scopes</span>
    )
  }

  const { capabilities, roles } = splitScopes(scopes)

  return (
    <div className='flex items-center gap-1 flex-wrap'>
      {capabilities.map(scope => (
        <LBBadge
          key={scope}
          variant={capabilityVariant}
          className='text-xs font-mono'
        >
          {scope}
        </LBBadge>
      ))}
      {roles.map(scope => (
        <RoleScopeBadgeWithTooltip key={scope} name={scope} />
      ))}
    </div>
  )
}

export default BackofficeScopeBadges
